import { useQuery } from "@tanstack/react-query";
import { useDatabase } from "@rapid/providers/database/DatabaseProvider";
import type { DatabaseAdapter } from "@rapid/providers/database/DatabaseAdapter";
import type { DatasetData, BatchData, SourceDocument } from "@rapid/utils/api/datasets";
import * as datasetsApi from "@rapid/utils/api/datasets";

export const datasetKeys = {
    all: ["datasets"] as const,
    byRapidDataId: (rapidDataId: string) =>
        [...datasetKeys.all, "byRapidDataId", rapidDataId] as const,
    batch: (datasetId: string, batchName: string) =>
        [...datasetKeys.all, "batch", datasetId, batchName] as const,
    sources: (datasetId: string, batchId: string) =>
        [...datasetKeys.all, "sources", datasetId, batchId] as const
};

/**
 * Query the dataset by rapidDataId directly from Firestore.
 * Shared between the react-query hook and imperative callers.
 */
export async function queryDatasetByRapidDataId(
    database: DatabaseAdapter,
    rapidDataId: string
): Promise<DatasetData | null> {
    const docs = await database.queryDocs("datasets", {
        field: "rapidDataId",
        op: "==",
        value: rapidDataId
    });
    if (docs.length === 0) return null;
    const doc = docs[0];
    return { ...doc.data(), id: doc.id } as DatasetData;
}

export function useDatasetByRapidDataId(rapidDataId: string | undefined) {
    const database = useDatabase();
    return useQuery<DatasetData | null>({
        queryKey: datasetKeys.byRapidDataId(rapidDataId!),
        queryFn: () => queryDatasetByRapidDataId(database, rapidDataId!),
        enabled: !!rapidDataId,
        staleTime: Infinity
    });
}

export function useBatchByName(datasetId: string | undefined, batchName: string) {
    return useQuery<BatchData | null>({
        queryKey: datasetKeys.batch(datasetId!, batchName),
        queryFn: () => datasetsApi.getBatchByName(datasetId!, batchName),
        enabled: !!datasetId,
        staleTime: Infinity
    });
}

export function useSourcesInBatch(datasetId: string | undefined, batchId: string | undefined) {
    return useQuery<SourceDocument[]>({
        queryKey: datasetKeys.sources(datasetId!, batchId!),
        queryFn: () => datasetsApi.listSourcesInBatch(datasetId!, batchId!),
        enabled: !!datasetId && !!batchId,
        staleTime: Infinity
    });
}
